/**
 * useExportProducts - Hook d'export CSV pour les produits
 *
 * Pattern: Component → Hook → Service (API)
 * Les produits ne sont pas paginés côté serveur, donc on exporte les données déjà chargées.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadCSV, formatDateForExport, formatNumberForExport, type ExportColumn } from '@/utils/exportUtils';
import { logger } from '@/utils/logger';
import { notify } from '@/stores/notificationStore';
import type { Product } from '@/types/models';

export function useExportProducts() {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback((products: Product[]) => {
    setIsExporting(true);
    try {
      const columns: ExportColumn<Product>[] = [
        { key: 'sku', header: t('products.fields.reference') },
        { key: 'name', header: t('products.fields.product') },
        {
          key: 'unitPrice',
          header: t('products.fields.priceHT'),
          formatter: (value) => formatNumberForExport(Number(value) || 0),
        },
        { key: 'unit', header: t('products.fields.unit') },
        {
          key: 'productType',
          header: t('products.export.type'),
          formatter: (value) => value ? t(`products.nature.${value}`, { defaultValue: String(value) }) : '',
        },
        {
          key: 'costPrice',
          header: t('products.composite.costPrice'),
          formatter: (value) => value != null ? formatNumberForExport(Number(value)) : '',
        },
        {
          key: 'marginPercent',
          header: t('products.composite.margin'),
          formatter: (value) => value != null ? formatNumberForExport(Number(value)) : '',
        },
        {
          key: 'stockQuantity',
          header: t('products.stock.stockLabel'),
          formatter: (value) => value != null ? formatNumberForExport(Number(value)) : '',
        },
        { key: 'currencyCode', header: t('products.export.currency') },
        {
          key: 'isActive',
          header: t('common.labels.status'),
          formatter: (value) => value ? t('common.labels.active') : t('common.labels.inactive'),
        },
        {
          key: 'createdAt',
          header: t('common.labels.createdAt'),
          formatter: (value) => formatDateForExport(value as string),
        },
      ];

      const today = new Date().toISOString().split('T')[0];
      downloadCSV(products, columns, {
        filename: `produits_export_${today}`,
        format: 'csv',
      });
    } catch (error) {
      logger.error('Export products error:', error);
      notify.error(t('common.export.error'));
    } finally {
      setIsExporting(false);
    }
  }, [t]);

  return { exportCSV, isExporting };
}
